import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'omega3Index', 'epa', 'dha',
    'indexDisplay', 'epaDisplay', 'dhaDisplay', 'componentDisplay',
    'statusContainer', 'statusDot', 'statusName', 'statusDescription',
    'supplementGuidance'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const omega3Index = parseFloat(this.omega3IndexTarget.value) || 0

    if (omega3Index <= 0) {
      return
    }

    this.indexDisplayTarget.textContent = omega3Index.toFixed(1)

    // Hide component display when using direct index input
    if (this.hasComponentDisplayTarget) {
      this.componentDisplayTarget.style.display = 'none'
    }

    // Get interpretation
    const result = this.interpret(omega3Index)
    this.updateDisplay(result)
  }

  calculateFromComponents() {
    const epa = parseFloat(this.epaTarget.value) || 0
    const dha = parseFloat(this.dhaTarget.value) || 0

    if (epa <= 0 && dha <= 0) {
      return
    }

    const omega3Index = epa + dha

    // Update the main index field
    this.omega3IndexTarget.value = omega3Index.toFixed(1)
    this.indexDisplayTarget.textContent = omega3Index.toFixed(1)

    // Show and update component display
    if (this.hasComponentDisplayTarget) {
      this.componentDisplayTarget.style.display = 'grid'
      this.epaDisplayTarget.textContent = epa.toFixed(1)
      this.dhaDisplayTarget.textContent = dha.toFixed(1)
    }

    // Get interpretation
    const result = this.interpret(omega3Index)
    this.updateDisplay(result)
  }

  interpret(value) {
    if (value < 4) {
      return {
        name: 'HIGH RISK',
        color: 'red-500',
        borderColor: 'border-red-500',
        bgColor: 'bg-red-50',
        description: 'Your Omega-3 Index is in the high-risk zone. ' +
          'This level is associated with significantly elevated cardiovascular risk, ' +
          'including higher rates of sudden cardiac death.',
        guidance: 'Aggressive supplementation recommended. ' +
          'Consider 2,000-3,000 mg combined EPA+DHA daily. ' +
          'Add 2-3 servings of fatty fish weekly. Retest in 3-4 months to track progress.'
      }
    }
    if (value < 6) {
      return {
        name: 'MODERATE RISK',
        color: 'orange-500',
        borderColor: 'border-orange-500',
        bgColor: 'bg-orange-50',
        description: 'Below optimal but improving. Your cardiovascular risk is still elevated compared to those with higher levels.',
        guidance: 'Increase omega-3 intake. Consider 1,500-2,000 mg EPA+DHA daily. Increase fatty fish consumption. Retest in 4-6 months.'
      }
    }
    if (value < 8) {
      return {
        name: 'INTERMEDIATE',
        color: 'yellow-500',
        borderColor: 'border-yellow-500',
        bgColor: 'bg-yellow-50',
        description: 'Approaching optimal levels. You\'re above average but not yet in the cardioprotective zone.',
        guidance: 'Continue current intake or modestly increase. 1,000-1,500 mg EPA+DHA daily should help reach optimal range. Retest in 6 months.'
      }
    }
    if (value <= 12) {
      return {
        name: 'OPTIMAL',
        color: 'green-500',
        borderColor: 'border-green-500',
        bgColor: 'bg-green-50',
        description: 'Excellent! Your Omega-3 Index is in the cardioprotective range ' +
          'associated with the lowest cardiovascular risk and best health outcomes.',
        guidance: 'Maintain current intake. 500-1,000 mg EPA+DHA daily for maintenance. ' +
          'Annual testing is sufficient. Great work!'
      }
    }
    return {
      name: 'VERY HIGH',
      color: 'blue-500',
      borderColor: 'border-blue-500',
      bgColor: 'bg-blue-50',
      description: 'Your Omega-3 Index is above the typical optimal range. ' +
        'This is still considered beneficial with no documented concerns at these levels.',
      guidance: 'Levels this high are typically seen with aggressive supplementation. ' +
        'You may be able to reduce your dose while maintaining optimal status. ' +
        'Consider maintaining with 500-1,000 mg daily.'
    }
  }

  updateDisplay(result) {
    this.statusDotTarget.className = `w-4 h-4 bg-${result.color}`
    this.statusNameTarget.textContent = result.name
    this.statusDescriptionTarget.textContent = result.description
    this.supplementGuidanceTarget.textContent = result.guidance
    this.statusContainerTarget.className = `p-6 ${result.borderColor} border-2 ${result.bgColor}`
  }
}
